/**
 * characterAccents — pure, engine-free helpers for Q-038 character
 * readability accents (Minna's procedural glowing eyes, Basil's brass
 * harness emissive). Kept free of scene/engine imports so unit tests run
 * without Havok/WebGL — the mesh/material work happens in Minna.ts /
 * Basil.ts via the PlayerEntity.onModelLoaded hook.
 */
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

/** Warm brass self-light — dimmed to prevent GlowLayer blooming into yellow artifacts (was 0.30/0.18/0.05). */
export const HARNESS_BRASS_EMISSIVE = new Color3(0.15, 0.10, 0.03);

/**
 * Q-038: add a dim emissive to Basil's Harness_Brass material(s). Matches by
 * substring because instantiateModelsToScene clones materials as
 * "Clone of Harness_Brass". Skips materials with authored emissive. Returns
 * the number of distinct materials touched (testability).
 */
export function applyHarnessBrassEmissive(
  meshes: AbstractMesh[],
  emissive: Color3 = HARNESS_BRASS_EMISSIVE,
): number {
  let touched = 0;
  const seen = new Set<unknown>();
  for (const mesh of meshes) {
    const mat = mesh.material as { name?: string; emissiveColor?: Color3 } | null;
    if (!mat?.name || !/harness_brass/i.test(mat.name) || seen.has(mat)) continue;
    seen.add(mat);
    const e = mat.emissiveColor;
    if (e && (e.r > 0.01 || e.g > 0.01 || e.b > 0.01)) continue; // respect authored emissive
    mat.emissiveColor = emissive.clone();
    touched++;
  }
  return touched;
}

/**
 * Fallback for untextured harness materials so they don't render as pure white blocks.
 */
export function applyHarnessAlbedoFallback(meshes: AbstractMesh[]): void {
  const seen = new Set<unknown>();
  for (const mesh of meshes) {
    const mat = mesh.material as any;
    if (!mat?.name || !/harness/i.test(mat.name) || seen.has(mat)) continue;
    seen.add(mat);
    if (mat.albedoTexture) continue;
    const c = mat.albedoColor;
    if (!c || (c.r < 0.95 || c.g < 0.95 || c.b < 0.95)) continue;
    
    if (/brass|cuff/i.test(mat.name)) {
      mat.albedoColor = Color3.FromHexString("#c49b3c"); // COLORS.BRASS equivalent
    } else if (/leather|strap/i.test(mat.name)) {
      mat.albedoColor = new Color3(0.25, 0.12, 0.05); // Dark brown leather
    } else {
      mat.albedoColor = new Color3(0.2, 0.2, 0.2); // Generic dark metal for pistons/junctions
    }
  }
}

/** Tunables for Minna's procedural glowing eyes (verify via cats-babylon-scene-qa screenshots). */
export const MINNA_EYE = {
  // Measured 2026-09-26 by mapping the green eye texels of
  // minna_stylized_whiskers.glb's texture back onto the mesh (eye centres at
  // Blender (0.087|0.229, -0.879, 0.476)), as fractions of the WHISKER-FREE
  // bbox — Minna.ts leaves whisker meshes out so they can't move the eyes.
  // The previous 0.78 forward fraction put the glow on the back of her head.
  HEIGHT_FRACTION: 0.81,     // eye line, fraction of bbox height from the floor
  FORWARD_FRACTION: 0.964,   // toward the face along +X at load
  // Her head is turned slightly in the source pose: the eye pair's midpoint
  // sits off the body's centre plane, as a fraction of bbox depth (z at load).
  CENTER_Z_FRACTION: -0.187,
  LATERAL_OFFSET: 0.055,     // world units, ± from the eye-pair midpoint
  DIAMETER: 0.1,             // world units
} as const;

export interface BoundsLike {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}

/**
 * World-space eye centers from the model's world-space bounds at load time
 * (model faces +X after PlayerEntity applies modelBaseRotationY = PI/2).
 */
export function computeEyePlacements(bb: BoundsLike): [
  { x: number; y: number; z: number },
  { x: number; y: number; z: number },
] {
  const x = bb.min.x + (bb.max.x - bb.min.x) * MINNA_EYE.FORWARD_FRACTION;
  const y = bb.min.y + (bb.max.y - bb.min.y) * MINNA_EYE.HEIGHT_FRACTION;
  const zMid = (bb.min.z + bb.max.z) / 2 + (bb.max.z - bb.min.z) * MINNA_EYE.CENTER_Z_FRACTION;
  return [
    { x, y, z: zMid - MINNA_EYE.LATERAL_OFFSET },
    { x, y, z: zMid + MINNA_EYE.LATERAL_OFFSET },
  ];
}
